import { SERVER_URL, syncedGet, syncedSet, syncedRemove } from '../data/serverStore';
import React, { useState, useEffect } from 'react';


// ── Storage ───────────────────────────────────────────────────────────────────
const SETTINGS_KEY = 'sl_api_settings';

export function getApiSettings() {
  try {
    const v = (syncedGet(SETTINGS_KEY, null) !== null ? JSON.stringify(syncedGet(SETTINGS_KEY, null)) : null);
    return v ? JSON.parse(v) : {};
  } catch { return {}; }
}

function saveApiSettings(s) {
  syncedSet(SETTINGS_KEY, s);
}

// ── Helper components ─────────────────────────────────────────────────────────
function Section({ icon, title, subtitle, children, accent = '#1e3060' }) {
  return (
    <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden',
      border: '1px solid #e2e6f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      marginBottom: 16 }}>
      <div style={{ background: accent, color: 'white', padding: '12px 18px',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)',
            marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, value, onChange, type = 'text',
  placeholder, mono, wide, secret }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#444',
        textTransform: 'uppercase', letterSpacing: '0.4px',
        display: 'block', marginBottom: 4 }}>{label}</label>
      {hint && <div style={{ fontSize: 11, color: '#888',
        marginBottom: 5, lineHeight: 1.5 }}>{hint}</div>}
      <div style={{ position: 'relative' }}>
        <input
          type={secret && !show ? 'password' : 'text'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: wide ? '100%' : 420,
            border: '1px solid #cdd3e4', borderRadius: 5,
            padding: secret ? '8px 40px 8px 10px' : '8px 10px',
            fontSize: mono ? 12 : 13,
            fontFamily: mono ? 'monospace' : 'inherit',
            outline: 'none', boxSizing: 'border-box',
            background: value ? '#fafffe' : 'white',
            borderColor: value ? '#1a7dc4' : '#cdd3e4',
          }}
        />
        {secret && (
          <button type="button" onClick={() => setShow(v => !v)}
            style={{ position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)', background: 'none',
              border: 'none', cursor: 'pointer', fontSize: 15, color: '#888' }}>
            {show ? '🙈' : '👁'}
          </button>
        )}
      </div>
    </div>
  );
}

function StatusDot({ ok, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600,
      color: ok ? '#1a6b42' : '#b83232' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%',
        background: ok ? '#1a6b42' : '#b83232',
        display: 'inline-block' }}/>
      {label}
    </span>
  );
}

function StepBox({ steps }) {
  return (
    <div style={{ background: '#f8f9ff', border: '1px solid #e0e4ec',
      borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 10,
          marginBottom: i < steps.length - 1 ? 8 : 0 }}>
          <span style={{ width: 20, height: 20, borderRadius: '50%',
            background: '#1e3060', color: 'white', fontSize: 11,
            fontWeight: 700, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
          <div style={{ fontSize: 12, color: '#444', lineHeight: 1.5,
            paddingTop: 1 }}
            dangerouslySetInnerHTML={{ __html: s }}/>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ApiSettings({ toast }) {
  const [s, setS] = useState(() => getApiSettings());
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState({});

  const set = (key, val) => setS(prev => ({ ...prev, [key]: val }));

  const handleSave = () => {
    saveApiSettings(s);
    setSaved(true);
    toast('success', 'API settings saved');
    setTimeout(() => setSaved(false), 2500);
  };

  // ── Test Gmail SMTP ────────────────────────────────────────────────────────
  const testGmail = async () => {
    setTesting(p => ({ ...p, gmail: 'testing' }));
    try {
      const res = await fetch(`${SERVER_URL}/test-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gmailUser: s.gmailUser,
          gmailAppPassword: s.gmailAppPassword,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast('success', '✅ Gmail connection successful');
        setTesting(p => ({ ...p, gmail: 'ok' }));
      } else {
        toast('error', '❌ Gmail failed: ' + data.error);
        setTesting(p => ({ ...p, gmail: 'fail' }));
      }
    } catch {
      toast('error', '❌ Email server not running — start it first');
      setTesting(p => ({ ...p, gmail: 'fail' }));
    }
  };

  // ── Test Google Drive ──────────────────────────────────────────────────────
  const testDrive = () => {
    if (!s.googleClientId) { toast('error', 'Enter Google Client ID first'); return; }
    toast('info', 'Open Admin → Auto-Backup → Connect Google Drive to test');
  };

  const gmailOk  = !!(s.gmailUser && s.gmailAppPassword);
  const driveOk  = !!(s.googleClientId);
  const serverOk = testing.gmail === 'ok';

  return (
    <div style={{ overflowY: 'auto', height: '100%',
      padding: '16px 20px', background: '#eef0f5' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e3060' }}>
            🔑 API & Integration Settings
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            Integration settings are stored on the LIMS server and shared
            across authorized workstations.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && (
            <span style={{ fontSize: 12, color: '#1a6b42', fontWeight: 600 }}>
              ✅ Saved
            </span>
          )}
          <button
            onClick={handleSave}
            style={{ background: '#1e3060', color: 'white', border: 'none',
              borderRadius: 6, padding: '9px 24px', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
            💾 Save All Settings
          </button>
        </div>
      </div>

      {/* Status row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20,
        padding: '10px 16px', background: 'white', borderRadius: 8,
        border: '1px solid #e2e6f0', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>Status:</span>
        <StatusDot ok={gmailOk}  label={gmailOk  ? 'Gmail configured' : 'Gmail not set'} />
        <StatusDot ok={serverOk} label={serverOk ? 'Email server verified' : 'Email server not tested'} />
        <StatusDot ok={driveOk}  label={driveOk  ? 'Google Drive ready' : 'Google Drive not set'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
        alignItems: 'start' }}>

        {/* ── Left column ── */}
        <div>

          {/* Gmail / Email */}
          <Section icon="📧" title="Gmail — Send Emails & Reports"
            subtitle="Used to email analytical reports and invoices directly from the app"
            accent="#1a3060">

            <StepBox steps={[
              'Go to <a href="https://myaccount.google.com/security" target="_blank" style="color:#1a7dc4">myaccount.google.com/security</a> and enable <strong>2-Step Verification</strong>',
              'Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" style="color:#1a7dc4">myaccount.google.com/apppasswords</a>',
              'Select app: <strong>Mail</strong>, device: <strong>Windows Computer</strong> → click <strong>Generate</strong>',
              'Copy the 16-character password and paste below (remove spaces)',
            ]} />

            <Field
              label="Gmail Address"
              hint="The address emails will be sent FROM"
              value={s.gmailUser}
              onChange={v => set('gmailUser', v)}
              placeholder="customerservice@sterlinganalytical.com"
            />
            <Field
              label="Gmail App Password"
              hint="16-character app password (NOT your regular Gmail password)"
              value={s.gmailAppPassword}
              onChange={v => set('gmailAppPassword', v.replace(/\s/g, ''))}
              placeholder="abcdefghijklmnop"
              mono secret
            />
            <Field
              label="From Name (optional)"
              hint="Display name shown to email recipients"
              value={s.fromName}
              onChange={v => set('fromName', v)}
              placeholder="Sterling Analytical Laboratory"
            />
            <Field
              label="Default CC (optional)"
              hint="Always CC this address on every report email"
              value={s.defaultCc}
              onChange={v => set('defaultCc', v)}
              placeholder="manager@sterlinganalytical.com"
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary" onClick={handleSave}>
                💾 Save
              </button>
              <button className="btn" onClick={testGmail}
                disabled={testing.gmail === 'testing' || !gmailOk}>
                {testing.gmail === 'testing' ? '⏳ Testing...' : '🔌 Test Connection'}
              </button>
              {testing.gmail === 'ok' && <StatusDot ok label="Connected ✓" />}
              {testing.gmail === 'fail' && <StatusDot label="Failed ✗" />}
            </div>

            <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0f4ff',
              borderRadius: 4, fontSize: 11, color: '#555' }}>
              💡 After saving, the email server needs to pick up these settings.
              Open a terminal and run:
              <code style={{ display: 'block', background: '#1e3060', color: 'white',
                padding: '4px 10px', borderRadius: 3, marginTop: 4, fontSize: 11 }}>
                cd sterling-lims/server &amp;&amp; npm start
              </code>
            </div>
          </Section>

        </div>

        {/* ── Right column ── */}
        <div>

          {/* Google Drive */}
          <Section icon="☁️" title="Google Drive — Auto-Backup Storage"
            subtitle="Automatically saves backups to your Google Drive every 24 hours"
            accent="#1a5276">

            <StepBox steps={[
              'Go to <a href="https://console.cloud.google.com" target="_blank" style="color:#1a7dc4">console.cloud.google.com</a> and create or open a project',
              'Go to <strong>APIs &amp; Services → OAuth consent screen</strong> → set to External → fill in app name → add your email as test user',
              'Go to <strong>APIs &amp; Services → Credentials → Create Credentials → OAuth Client ID</strong>',
              'Application type: <strong>Web application</strong> · Add <code>http://localhost:3000</code> as Authorized JavaScript Origin',
              'Copy the <strong>Client ID</strong> (ends in .apps.googleusercontent.com) and paste below',
              'Enable <strong>Google Drive API</strong>: APIs &amp; Services → Library → search "Drive API" → Enable',
            ]} />

            <Field
              label="Google Client ID"
              hint="Ends with .apps.googleusercontent.com"
              value={s.googleClientId}
              onChange={v => set('googleClientId', v.trim())}
              placeholder="123456789-abcdefg.apps.googleusercontent.com"
              mono wide secret
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary" onClick={handleSave}>
                💾 Save
              </button>
              <button className="btn" onClick={testDrive} disabled={!driveOk}>
                🔌 Test Drive
              </button>
            </div>

            <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0f4ff',
              borderRadius: 4, fontSize: 11, color: '#555', lineHeight: 1.6 }}>
              💡 After saving, go to <strong>Admin → 🔄 Auto-Backup</strong> and click
              <strong> Connect Google Drive</strong> to authorize.
              A Google sign-in popup will appear — sign in with your work account.
            </div>
          </Section>

          {/* Email server sync note */}
          <Section icon="⚙️" title="Email Server — Sync Settings"
            subtitle="Push your saved Gmail credentials to the running email server"
            accent="#2a6049">

            <p style={{ fontSize: 12, color: '#666', marginBottom: 12, lineHeight: 1.6 }}>
              The email server reads credentials at startup.
              If you change your Gmail settings above, sync them to the server:
            </p>

            <button className="btn btn-primary" onClick={async () => {
              const current = getApiSettings();
              try {
                const res = await fetch(`${SERVER_URL}/update-credentials`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    gmailUser: current.gmailUser,
                    gmailAppPassword: current.gmailAppPassword,
                    fromName: current.fromName,
                  }),
                });
                const data = await res.json();
                if (data.success) toast('success', '✅ Email server credentials updated');
                else toast('error', '❌ ' + (data.error || 'Sync failed'));
              } catch {
                toast('error', 'Email server not running — start it in a terminal first');
              }
            }}>
              🔄 Sync to Email Server
            </button>

            <div style={{ marginTop: 10, fontSize: 11, color: '#888', lineHeight: 1.6 }}>
              Or simply restart the email server after saving credentials —
              it reads the latest settings on startup.
            </div>
          </Section>

        </div>
      </div>

      {/* Save button at bottom */}
      <div style={{ display: 'flex', justifyContent: 'flex-end',
        paddingTop: 8, paddingBottom: 20 }}>
        <button onClick={handleSave}
          style={{ background: '#1e3060', color: 'white', border: 'none',
            borderRadius: 6, padding: '10px 32px', cursor: 'pointer',
            fontSize: 14, fontWeight: 700 }}>
          💾 Save All Settings
        </button>
      </div>
    </div>
  );
}
